// src/components/Synopsis.js
import React, { useState, useEffect, useContext } from 'react';
import {
  Layout,
  Row,
  Col,
  Card,
  Button,
  Divider,
  List,
  Avatar,
  Typography,
  message,
  Spin,
  Badge,
  Collapse,
  Select,
  Tooltip,
  Empty,
  Input,
  DatePicker,
  Space,
  Modal,
  Form,
  Tabs,  // NEW: added Tabs import
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  FileTextOutlined,
  FolderViewOutlined,
  ProfileOutlined,
  LoadingOutlined,
  RedoOutlined,
  DeleteOutlined,
  SearchOutlined,
  CaretRightOutlined,
  CopyOutlined,
  ExpandOutlined, // NEW: for maximizer icon
  CheckOutlined,       // NEW: for approval
  CloseOutlined,       // NEW: for rejection
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import playIcon from '../../assets/images/play-icon.png';
import Waveform from './Waveform/Waveform';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { loadStripe } from '@stripe/stripe-js';
import IconButtonGroup from './IconButtonGroup/IconButtonGroup';
import RecordingModal from './RecordingModal/RecordingModal';
import { useAuth } from '../../auth/AuthContext';
import EditPatientModal from '../PatientDetails/EditPatientModal/EditPatientModal';
import AssessmentModal from './AssessmentModal/AssessmentModal';
import PatientNoteModal from './PatientNoteModal/PatientNoteModal';
import StickyChat from './StickyChat/StickyChat';
import ConfirmDeletePatientModal from './ConfirmDeletePatientModal/ConfirmDeletePatientModal';
import ThemeContext from '../../context/ThemeContext';
import '../../assets/css/ModalStyle.css';
import AddTemplateModal from '../TemplateSelector/CustomTemplate/YourCustomTemplate/AddTemplateModal/AddTemplateModal';
import PaymentModal from '../PaymentModal/PaymentModal';
import {useNavigate} from "react-router-dom"; // Ensure correct path

const { Content } = Layout;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;
const { Text } = Typography;

// ====================
// NEW: Helper functions & components for Markdown splitting & rendering
// ====================

// Splits the markdown into sections using the following hierarchy:
// 1. If headings (#, ##, etc.) exist, split on them.
// 2. Otherwise, if a delimiter ('---') exists, use that.
// 3. Otherwise, fallback to splitting on lines ending with a colon.
function splitMarkdownIntoSections(markdown) {
  if (/^#{1,6}\s/m.test(markdown)) {
    const lines = markdown.split('\n');
    const sections = [];
    let currentSection = [];
    lines.forEach((line) => {
      if (/^#{1,6}\s/.test(line) && currentSection.length > 0) {
        sections.push(currentSection.join('\n').trim());
        currentSection = [line];
      } else {
        currentSection.push(line);
      }
    });
    if (currentSection.length > 0) {
      sections.push(currentSection.join('\n').trim());
    }
    return sections;
  } else if (markdown.includes('\n---\n')) {
    return markdown
      .split('\n---\n')
      .map((section) => section.trim())
      .filter((section) => section.length > 0);
  } else {
    const lines = markdown.split('\n');
    const sections = [];
    let currentSection = [];
    lines.forEach((line) => {
      if (!line.trim().startsWith('-') && line.trim().endsWith(':')) {
        if (currentSection.length > 0) {
          sections.push(currentSection.join('\n').trim());
          currentSection = [];
        }
      }
      currentSection.push(line);
    });
    if (currentSection.length > 0) {
      sections.push(currentSection.join('\n').trim());
    }
    return sections;
  }
}

const markdownToPlainText = (markdown) => {
  return markdown
    .replace(/[#*_>`~]/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/!\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\n\s*\n/g, '\n\n')
    .trim();
};

// A card component that renders one markdown section with a copy icon.
const MarkdownSectionCard = ({ sectionContent }) => {
  const rawTitle = sectionContent.split('\n').find((line) => line.trim() !== '') || 'Section';
  const copiedSectionContent = markdownToPlainText(sectionContent);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copiedSectionContent);
      message.success('Section copied to clipboard!');
    } catch (error) {
      message.error('Failed to copy section.');
    }
  };

  return (
    <div style={{ padding: '10px' }}>
      <div style={{ textAlign: 'right', marginBottom: '-20px' }}>
        <Tooltip title="Copy Section">
          <CopyOutlined onClick={handleCopy} style={{ cursor: 'pointer', fontSize: '16px' }} />
        </Tooltip>
      </div>
      <ReactMarkdown>{sectionContent}</ReactMarkdown>
      <hr />
    </div>
  );
};

// Component that receives a markdown string, splits it, and renders each section.
const MarkdownSectionRenderer = ({ markdown }) => {
  if (!markdown) return <Empty description="No Content Found" />;
  const sections = splitMarkdownIntoSections(markdown);
  return (
    <>
      {sections.map((section, index) => (
        <MarkdownSectionCard key={index} sectionContent={section} />
      ))}
    </>
  );
};
// ====================
// End of NEW markdown splitting integration
// ====================

// NEW: ICD-10 Code Item Component
const ICD10CodeItem = ({ code, status, onApprove, onReject }) => {
  return (
    <div
      style={{
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
        padding: '8px 0',
      }}
    >
      <div>
        <Typography.Text strong>
          {code.name} ({code.icd10_code})
        </Typography.Text>
        <div style={{ fontSize: '12px' }}>{code.evidence}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <Tooltip title="Copy">
          <CopyOutlined
            onClick={() => {
              navigator.clipboard.writeText(
                `${code.name} (${code.icd10_code}): ${code.evidence}`
              );
              message.success('Copied!');
            }}
            style={{
              backgroundColor: 'transparent',
              color: '#2196F3',
              borderColor: 'transparent',
              width: '30px',
              height: '30px',
              boxShadow: 'none',
              cursor: 'pointer',
            }}
          />
        </Tooltip>
        {status !== 'approved' && (
          <Tooltip title="Approve">
            <Button size="small" icon={<CheckOutlined />} onClick={() => onApprove(code.id)} style={{ backgroundColor: "transparent", color: '#45c655', borderColor: 'transparent', width: "30px", height: "30px", boxShadow: "none", cursor: "pointer"  }}
          />
          </Tooltip>
        )}
        {status !== 'rejected' && (
          <Tooltip title="Reject">
            <Button size="small" icon={<CloseOutlined />} onClick={() => onReject(code.id)} style={{ marginLeft: 8, backgroundColor: "transparent", color: '#fc320a', borderColor: 'transparent', width: "30px", height: "30px", boxShadow: "none", cursor: "pointer"  }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};


const ICD10CodeItemReadOnly = ({ code, status, onApprove, onReject }) => {
  return (
    <div
      style={{
        // display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
        padding: '8px 0',
      }}
    >
      <div>
        <Typography.Text strong>
          {code.name} ({code.icd10_code})
        </Typography.Text>
        <div style={{ fontSize: '12px' }}>{code.evidence}</div>
      </div>
      <div style={{textAlign: "right"}}>
        <Tooltip title="Copy">
          <CopyOutlined
            onClick={() => {
              navigator.clipboard.writeText(`${code.name} (${code.icd10_code}): ${code.evidence}`);
              message.success('Copied!');
            }}
            style={{ backgroundColor: "transparent", color: '#2196F3', borderColor: 'transparent', width: "30px", height: "30px", boxShadow: "none", cursor: "pointer"  }}
          />
        </Tooltip>
      </div>
    </div>
  );
};

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Synopsis = () => {
  // Retrieve user details from localStorage
  const userDetails = localStorage.getItem('userDetails');
  const user = userDetails && JSON.parse(userDetails);
  const auth = useAuth();
  const { themeMode } = useContext(ThemeContext);

  // State Management
  const [modalVisible, setModalVisible] = useState(false);
  const [finalResponse, setFinalResponse] = useState(null);
  const [data, setData] = useState([]);
  const [patientInterview, setPatientInterview] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [patientDataLoading, setPatientDataLoading] = useState(null);
  const [patientInterviewLoading, setPatientInterviewLoading] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [editedSummary, setEditedSummary] = useState(null);
  const [aiGenerated, setAIGenerated] = useState(null);
  const [editSummaryLoading, setEditedSummaryLoading] = useState(false);
  const [activePanel, setActivePanel] = useState('01');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [isPatientNoteModalOpen, setIsPatientNoteModalOpen] = useState(false);
  const [newSubmission, setNewSubmission] = useState(false);
  const [synopsisLoading, setSynopsisLoading] = useState(false);
  const [transcriptionLoading, setTranscriptionLoading] = useState(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [deletePatientEntryLoading, setDeletePatientEntryLoading] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Payment and Subscription States
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [addTemplateModalVisible, setAddTemplateModalVisible] = useState(false);
  const [addTemplateSaving, setAddTemplateSaving] = useState(false);
  const [addTemplateForm] = Form.useForm();
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentAction, setPaymentAction] = useState('activate');

  // NEW: State for ICD-10 Codes and its fullscreen modal
  const [icd10Codes, setICD10Codes] = useState({ generated: [], accepted: [], rejected: [] });
  const [icd10Loading, setIcd10Loading] = useState(false);
  const [isICD10ModalOpen, setIsICD10ModalOpen] = useState(false);
  const [idsToBeApproved, setIdsToBeApproved] = useState([]);
  // At the top of your Synopsis component (inside the function)
  const [selectedApprovedIds, setSelectedApprovedIds] = useState([]);
  const [selectedRejectedIds, setSelectedRejectedIds] = useState([]);


  const [activeKey, setActiveKey] = useState(['icd10']);

  const navigate = useNavigate();

  const handleCollapseChange = (key) => {
    // If no panel is open, automatically open panel "2"
    if (key.length === 0) {
      setActiveKey(['icd10']); // choose the key of the panel you want to open
    } else {
      setActiveKey(key);
    }
  };

  const ICD10CodeItemWithCheckbox = ({ code, status, onApprove, onReject, onSelect, selected }) => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0',
          padding: '8px 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Checkbox for selection */}
          <Checkbox
            checked={idsToBeApproved.includes(code.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setIdsToBeApproved((prev) => [...prev, code.id]);
              } else {
                setIdsToBeApproved((prev) => prev.filter((id) => id !== code.id));
              }

            }}
            style={{ marginRight: 8 }}
          />
          <div>
            <Typography.Text strong>
              {code.name} ({code.icd10_code})
            </Typography.Text>
            <div style={{ fontSize: '12px' }}>{code.evidence}</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Tooltip title="Copy">
            <CopyOutlined
              onClick={() => {
                navigator.clipboard.writeText(
                  `${code.name} (${code.icd10_code}): ${code.evidence}`
                );
                message.success('Copied!');
              }}
              style={{
                backgroundColor: 'transparent',
                color: '#2196F3',
                borderColor: 'transparent',
                width: '30px',
                height: '30px',
                boxShadow: 'none',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  // Handle Payment
  const handlePaymentAction = async () => {
    setPaymentLoading(true);
    try {
      if (paymentAction === 'reactivate') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/reactivate-subscription`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${auth.user['access_token']}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const result = await response.json();
          message.success(result.message || 'Subscription reactivated successfully.');
          handlePaymentSuccess();
        } else {
          const errorData = await response.json();
          message.error(`Failed to reactivate subscription: ${errorData.detail || 'Unknown error'}`);
        }
      } else {
        const price_id = `${process.env.REACT_APP_PLAN_ID}`;
        const plan_name = `${process.env.REACT_APP_PLAN_NAME}`;
        const formData = new FormData();
        formData.append('price_id', price_id);
        formData.append('plan_name', plan_name);
        formData.append('action', paymentAction);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/create-checkout-session`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${auth.user['access_token']}`,
          },
          body: formData,
        });
        if (response.ok) {
          const result = await response.json();
          const { sessionId } = result;
          const stripe = await stripePromise;
          const { error } = await stripe.redirectToCheckout({ sessionId });
          if (error) {
            message.error(error.message);
          }
        } else {
          const errorData = await response.json();
          message.error(`Failed to create checkout session: ${errorData.detail || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error handling payment action:', error);
      message.error('Error handling payment action.');
    }
    setPaymentLoading(false);
  };

  const handlePaymentSuccess = () => {
    setIsPaymentModalOpen(false);
    checkSubscription();
    if (paymentAction === 'activate') {
      setAddTemplateModalVisible(true);
    }
  };

  // Handle Payment Modal Cancel
  const handlePaymentCancel = () => {
    if (user.first_login) {
      message.warning('Subscription is required to access all features.');
      setAddTemplateModalVisible(true);
    }
    setIsPaymentModalOpen(false);
    auth.logout();
    navigate('/login');
  };

  // Payment status check and session verification...
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const status = urlParams.get('status');
    if (sessionId && status === 'success') {
      verifySession(sessionId);
    } else if (status === 'cancelled') {
      message.error('Payment was not successful. Please subscribe to continue using all features.');
      setIsPaymentModalOpen(true);
    } else {
      const fetchUserDetails = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/get_user`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${auth.user['access_token']}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            const expiryDate = new Date(data.subscription_expiry_date);
            const hasExpired = expiryDate < new Date();
            if (data.subscription_status) {
              if (data.subscription_status !== 'active' && !(data.subscription_status === 'canceled' && !hasExpired)) {
                if (data.first_login) {
                  setIsPaymentModalOpen(true);
                } else {
                  message.warning("You don't have an active subscription. Please renew/reactivate your subscription to access all features.");
                }
              } else {
                setIsPaymentModalOpen(false);
                setHasActiveSubscription(true);
                if (data.first_login) {
                  setAddTemplateModalVisible(true);
                }
              }
            } else {
              setIsPaymentModalOpen(true);
            }
          }
        } catch (error) {
          console.error('Error fetching user details:', error);
        }
      };
      fetchUserDetails();
    }
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const verifySession = async (sessionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/session-status?session_id=${sessionId}`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'GET',
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'complete') {
          setHasActiveSubscription(true);
          setIsPaymentModalOpen(false);
          user.first_login && setAddTemplateModalVisible(true);
        } else {
          setHasActiveSubscription(false);
          setIsPaymentModalOpen(true);
          message.warning('You need an active subscription to access all features.');
        }
      } else {
        message.error('Failed to verify payment status.');
      }
    } catch (error) {
      console.error('Error verifying session', error);
      message.error('Error verifying payment.');
    }
  };

  // Initial Check for Subscription Status
  useEffect(() => {
    if (!user.first_login) {
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/subscription-status`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'GET',
      });
      if (response.ok) {
        const result = await response.json();
        const expiryDate = new Date(result.current_period_end);
        const hasExpired = expiryDate < new Date();
        if (result.current_status === 'active' || (result.current_status === 'canceled' && !hasExpired)) {
          setHasActiveSubscription(true);
        } else {
          setHasActiveSubscription(false);
        }
      } else {
        user.bypass_subscription !== true && message.error('No active subscription found');
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      message.error('Error checking subscription status.');
    }
  };

  // Handle AddTemplate Modal Save
  const handleAddTemplateSave = async (values) => {
    setAddTemplateSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('short_description', values.short_description);
      formData.append('public', values.public || false);
      if (values.file && values.file.length > 0) {
        formData.append('file', values.file[0].originFileObj);
      }
      if (values.free_text) {
        formData.append('free_text', values.free_text);
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/templates/create/file`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        message.success('Custom template created successfully!');
        const newTemplateId = result._id || (result.data && result.data._id);
        if (!newTemplateId) {
          throw new Error('Template ID not found in the response.');
        }
        const selectFormData = new FormData();
        selectFormData.append('template_id', newTemplateId);
        const selectResponse = await fetch(`${process.env.REACT_APP_API_URL}/templates/user/select`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${auth.user['access_token']}`,
          },
          body: selectFormData,
        });
        if (selectResponse.ok) {
          message.success('Template set as default successfully!');
        } else {
          const errorData = await selectResponse.json();
          message.error(`Failed to set default template: ${errorData.detail || 'Unknown error'}`);
        }
        const updateResponse = await fetch(`${process.env.REACT_APP_API_URL}/update_user/first_login`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${auth.user['access_token']}`,
          },
        });
        if (updateResponse.ok) {
          const updatedUser = { ...user, first_login: false };
          localStorage.setItem('userDetails', JSON.stringify(updatedUser));
          setHasActiveSubscription(true);
        } else {
          const errorData = await updateResponse.json();
          message.error(`Failed to update user status: ${errorData.detail || 'Unknown error'}`);
        }
        setAddTemplateModalVisible(false);
        addTemplateForm.resetFields();
      } else {
        const errorData = await response.json();
        message.error(`Failed to create template: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating custom template:', error);
      message.error('An error occurred while creating the template.');
    }
    setAddTemplateSaving(false);
  };

  // Handle AddTemplate Modal Cancel
  const handleAddTemplateCancel = async () => {
    try {
      if (user.first_login) {
        message.warning('You need to subscribe to access all features.');
      }
    } catch (error) {
      console.error('Error handling template cancel:', error);
      message.error('An error occurred while handling the template cancellation.');
    }
    setAddTemplateModalVisible(false);
    addTemplateForm.resetFields();
  };

  // Function to convert markdown to sanitized HTML
  const markdownToHtml = (markdown) => {
    const rawHtml = marked.parse(markdown);
    return DOMPurify.sanitize(rawHtml);
  };

  // Handle search functionality
  const handleSearch = async () => {
    fetchData(searchName, dateRange);
  };

  // Handle date range change
  const onDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch patient data when a new patient is selected
  useEffect(() => {
    if (selectedId) {
      setPatientInterview(false);
      setPatientDataLoading(true);
      setVersionLoading(true);
      fetchPatientData();
      fetchEditedVersions();
      // NEW: Fetch ICD-10 codes for the selected file
      fetchICD10Codes(false);
      setIdsToBeApproved([])
    }
  }, [selectedId]);

  // Fetch transcript and summary when there's a new submission
  useEffect(() => {
    if (newSubmission) {
      setSynopsisLoading(true);
      setTranscriptionLoading(true);
      fetchTranscript();
    }
  }, [newSubmission]);

  // Fetch selected edited version
  useEffect(() => {
    if (selectedVersion) {
      setEditedSummaryLoading(true);
      fetchSelectedEditedVersion();
    } else {
      setEditedSummary(null);
      setAIGenerated(null);
      setVersions([]);
    }
  }, [selectedVersion]);

  useEffect(() => {
    fetchTemplatesForSynopsis();
  }, []);

  useEffect(() => {
    if (finalResponse && finalResponse.template_id) {
      setSelectedTemplate(finalResponse.template_id);
    } else {
      setSelectedTemplate(null);
    }
  }, [finalResponse]);
  // >>> NEW: Helper function to fetch templates and set default preferred template <<<
  const fetchTemplatesForSynopsis = async () => {
    try {
      const response1 = await fetch(`${process.env.REACT_APP_API_URL}/templates`, {
        headers: { Authorization: `Bearer ${auth.user['access_token']}` },
        method: 'GET',
      });
      let communityTemplates = [];
      if (response1.ok) {
        const result1 = await response1.json();
        const userDetails = localStorage.getItem("userDetails");
        const user = userDetails ? JSON.parse(userDetails) : {};
        communityTemplates = result1.data.filter(
          (r) =>
            r.community_template &&
            ((r.user_id === null && r.public) || (r.user_id === user.user_id && !r.public))
        );
      }
      const response2 = await fetch(`${process.env.REACT_APP_API_URL}/templates/me`, {
        headers: { Authorization: `Bearer ${auth.user['access_token']}` },
        method: 'GET',
      });
      let customizedPredefinedTemplates = [];
      let yourCustomTemplates = [];
      if (response2.ok) {
        const result2 = await response2.json();
        const userDetails = localStorage.getItem("userDetails");
        const user = userDetails ? JSON.parse(userDetails) : {};
        const userId = user.user_id;
        customizedPredefinedTemplates = result2.data.filter(
          (r) => r.user_id === userId && r.type === 'customizedPredefined'
        );
        yourCustomTemplates = result2.data.filter(
          (r) => r.user_id === userId && r.type === 'customizedFromScratch'
        );
      }
      const groups = [];
      if (communityTemplates.length > 0) {
        groups.push({
          label: 'Existing Templates',
          options: communityTemplates.map((template) => ({
            value: template._id,
            label: template.name,
          })),
        });
      }
      if (customizedPredefinedTemplates.length > 0) {
        groups.push({
          label: 'Customized Predefined Templates',
          options: customizedPredefinedTemplates.map((template) => ({
            value: template._id,
            label: template.name,
          })),
        });
      }
      if (yourCustomTemplates.length > 0) {
        groups.push({
          label: 'Your Custom Templates',
          options: yourCustomTemplates.map((template) => ({
            value: template._id,
            label: template.name,
          })),
        });
      }
      setTemplateOptions(groups);
      try {
        const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_user`, {
          headers: { Authorization: `Bearer ${auth.user['access_token']}` },
          method: 'GET',
        });
        if (userResponse.ok) {
          const user = await userResponse.json();
          if (user.preferred_template_id) {
            setSelectedTemplate(user.preferred_template_id);
          } else if (groups.length > 0 && groups[0].options.length > 0) {
            setSelectedTemplate(groups[0].options[0].value);
          }
        } else if (groups.length > 0 && groups[0].options.length > 0) {
          setSelectedTemplate(groups[0].options[0].value);
        }
      } catch (err) {
        console.error("Error fetching user details:", err);
        if (groups.length > 0 && groups[0].options.length > 0) {
          setSelectedTemplate(groups[0].options[0].value);
        }
      }
    } catch (error) {
      console.error("Error fetching templates for Synopsis:", error);
      message.error("Error fetching templates.");
    }
  };

  // Function to fetch ICD-10 Codes with safe defaults
  const fetchICD10Codes = async (forcedRecreate = false) => {
    try {
      setIcd10Loading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/audio/${selectedId}/icd10_codes?forced_recreate=${forcedRecreate}`,
        {
          headers: {
            Authorization: `Bearer ${auth.user['access_token']}`,
          },
          method: 'GET',
        }
      );
      if (response.ok) {
        const result = await response.json();

        setICD10Codes({
          generated: result.generated_icd10_codes
            ? result.generated_icd10_codes
            : [],
          accepted: result.accepted_icd10_codes ? result.accepted_icd10_codes : [],
          rejected: result.rejected_icd10_codes ? result.rejected_icd10_codes : [],
        });
      } else {
        message.error('Failed to fetch ICD-10 codes');
      }
    } catch (error) {
      console.error('Error fetching ICD-10 codes', error);
      message.error('Error fetching ICD-10 codes');
    }
    setIcd10Loading(false);
  };

  // Function to update ICD-10 selection (approval/rejection)
  const updateICD10Selection = async (approvedIds) => {
    try {
      setIcd10Loading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/audio/${selectedId}/icd10_codes/selection`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${auth.user['access_token']}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(approvedIds),
        }
      );
      if (response.ok) {
        const result = await response.json();
        setICD10Codes((prev) => ({
          ...prev,
          accepted: result.accepted_icd10_codes,
          rejected: result.rejected_icd10_codes,
        }));
        message.success(result.message);
        setIdsToBeApproved([])
      } else {
        const errorData = await response.json();
        message.error(`Failed to update ICD-10 selection: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating ICD-10 selection', error);
      message.error('Error updating ICD-10 selection.');
    }
    setIcd10Loading(false);
  };

  const handleICD10Approve = async (codeId) => {
    let approvedIds = icd10Codes.accepted.map((item) => item.id);
    if (!approvedIds.includes(codeId)) {
      approvedIds.push(codeId);
    }
    await updateICD10Selection(approvedIds);
    setPatientDataLoading(true);
    fetchPatientData();
    setEditedSummaryLoading(true);
    fetchSelectedEditedVersion();
  };

  const handleICD10Reject = async (codeId) => {
    let approvedIds = icd10Codes.accepted.map((item) => item.id);
    if (approvedIds.includes(codeId)) {
      approvedIds = approvedIds.filter((id) => id !== codeId);
    }
    await updateICD10Selection(approvedIds);

    setPatientDataLoading(true);
    fetchPatientData();
    setEditedSummaryLoading(true);
    fetchSelectedEditedVersion();
  };

  // Fetch interview history
  const fetchData = async (searchName = '', dateRange = null) => {
    try {
      const params = new URLSearchParams();
      if (searchName) {
        params.append('name', searchName);
      }
      if (dateRange && dateRange.length === 2) {
        params.append('start_date', dateRange[0].toISOString());
        params.append('end_date', dateRange[1].toISOString());
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/files/audio?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'GET',
      });
      if (response.ok) {
        const result = await response.json();
        setData(result.data);
        if (result.data.length > 0) {
          setSelectedId(result.data[0]._id);
          setPatientId(result.data[0].patient_id);
        }
      } else {
        message.error('Failed to fetch interview history');
      }
    } catch (error) {
      console.error('Error fetching interview history', error);
      message.error('Error fetching interview history');
    }
    setPageLoading(false);
  };

  // Fetch specific patient data
  const fetchPatientData = async (fromTranscription = false) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/files/audio/${selectedId}`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'GET',
      });
      if (response.ok) {
        const result = await response.json();
        setFinalResponse(result);
        setSelectedLanguage(result.target_language_code);
      } else {
        message.error('Failed to fetch interview information');
      }
    } catch (error) {
      console.error('Error fetching interview information', error);
      message.error('Error fetching interview information');
    }
    setPageLoading(false);
    setPatientDataLoading(false);
    fromTranscription && setTranscriptionLoading(false);
  };

  // Placeholder for fetching patient interview
  const fetchPatientInterview = async () => {
    setPatientInterview(true);
    setPatientInterviewLoading(false);
  };

  // Fetch edited summary versions
  const fetchEditedVersions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/files/audio/${selectedId}/summary/versions`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'GET',
      });
      if (response.ok) {
        const result = await response.json();
        if (result.version_history) {
          const options = result.version_history.map((item) => ({
            label: item.version,
            value: item.version,
          }));
          setVersions(options);
          setSelectedVersion(options[options.length - 1].value);
        } else {
          setVersions([]);
          setSelectedVersion(null);
        }
      } else {
        message.error('Failed to fetch Edited Summary History');
      }
    } catch (error) {
      console.error('Error fetching Edited Summary History', error);
      message.error('Error fetching Edited Summary History');
    }
    setVersionLoading(false);
  };

  // Fetch selected edited summary version
  const fetchSelectedEditedVersion = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/audio/${selectedId}/summary/version/${selectedVersion}`,
        {
          headers: {
            Authorization: `Bearer ${auth.user['access_token']}`,
          },
          method: 'GET',
        }
      );
      if (response.ok) {
        const result = await response.json();
        setEditedSummary(result.summary);
        setAIGenerated(result.ai_generated);
      } else {
        message.error('Failed to fetch Edited Summary');
      }
    } catch (error) {
      console.error('Error fetching Edited Summary', error);
      message.error('Error fetching Edited Summary');
    }
    setEditedSummaryLoading(false);
  };

  // Handle sending email
  const handleSendEmail = async () => {
    setSendEmailLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/files/audio/${selectedId}/send_patient_note`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'POST',
      });
      if (response.ok) {
        const result = await response.json();
        message.success(result.message);
      } else {
        message.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email', error);
      message.error('Error sending email');
    }
    setSendEmailLoading(false);
  };

  // Fetch transcript
  const fetchTranscript = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v2/files/audio/${selectedId}/generate_transcript`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'POST',
      });
      if (response.ok) {
        const result = await response.json();
        await fetchPatientData(true);
        newSubmission && fetchSummary();
      } else {
        message.error('Failed to generate transcript');
        setNewSubmission(false);
        setTranscriptionLoading(false);
      }
    } catch (error) {
      console.error('Error generating transcript', error);
      message.error('Error generating transcript');
      setNewSubmission(false);
      setTranscriptionLoading(false);
    }
  };

  // Fetch summary
  const fetchSummary = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v2/files/audio/${selectedId}/generate_summary`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'POST',
      });
      if (response.ok) {
        const result = await response.json();
        fetchPatientData();
      } else {
        message.error('Failed to generate summary');
      }
    } catch (error) {
      console.error('Error generating summary', error);
      message.error('Error generating summary');
    }
    setSynopsisLoading(false);
    setNewSubmission(false);
  };

  // Handle deleting patient entry
  const handleDeletePatient = async () => {
    setDeletePatientEntryLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/files/audio/delete/${selectedId}`, {
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        method: 'DELETE',
      });
      if (response.ok) {
        const result = await response.json();
        message.success('Patient entry deleted successfully');
        fetchData();
      } else {
        message.error('Failed to delete patient entry');
      }
    } catch (error) {
      console.error('Error deleting patient entry', error);
      message.error('Error deleting patient entry');
    }
    setDeletePatientEntryLoading(false);
  };

  // Confirmation for Summary Regeneration
  const confirmRegenerateSummary = () => {
    Modal.confirm({
      title: 'Confirm Regeneration',
      content: 'Do you want to regenerate the summary?',
      okText: 'Yes',
      cancelText: 'No',
      centered: true,
      className: themeMode === 'dark' ? 'dark-modal' : 'light-modal',
      onOk: () => {
        setSynopsisLoading(true);
        fetchSummary();
      },
    });
  };

  // Confirmation for Transcript Regeneration
  const confirmRegenerateTranscript = () => {
    Modal.confirm({
      title: 'Confirm Regeneration',
      content: 'Do you want to regenerate the transcription?',
      okText: 'Yes',
      cancelText: 'No',
      centered: true,
      className: themeMode === 'dark' ? 'dark-modal' : 'light-modal',
      onOk: () => {
        setTranscriptionLoading(true);
        fetchTranscript();
      },
    });
  };

  // Confirmation for Fetching Interview Recording
  const confirmFetchInterview = () => {
    Modal.confirm({
      title: 'Confirm Action',
      content: 'Do you want to fetch the interview recording?',
      okText: 'Yes',
      cancelText: 'No',
      centered: true,
      className: themeMode === 'dark' ? 'dark-modal' : 'light-modal',
      onOk: () => {
        setPatientInterviewLoading(true);
        fetchPatientInterview(selectedId);
      },
    });
  };

  // Utility function to replace underscores with spaces
  function replaceUnderscoreWithSpace(name) {
    if (name.includes('_')) {
      return name.replace(/_/g, ' ');
    }
    return name;
  }

  // Conditionally disable features based on subscription status
  const isFeatureDisabled = !hasActiveSubscription;
  const [colWdith, setColWidth] = useState(14);
  const ratio = window.devicePixelRatio || 1;
  useEffect(() => {
    function handleZoom() {
      if (ratio >= 1.25) {
        setColWidth(24);
      } else {
        setColWidth(10);
      }
    }
    window.addEventListener('resize', handleZoom);
    handleZoom();
  }, [ratio]);
  return (
    <>
      <Content style={{ overflow: 'initial', maxHeight: '90vh', overflow: 'auto' }} className="site-layout-background">
        {pageLoading && versionLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Row style={{ height: '820px', display: 'flex', padding: '10px' }}>
            <Col lg={6} sm={24} xs={24} style={{ padding: '10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Card style={{ padding: '10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Row>
                  <Col span={24} style={{ height: '720px', overflowY: 'auto', overflowX: 'visible' }}>
                    <div style={{ textAlign: 'center' }}>
                      <Button
                        type="primary"
                        shape="round"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={() => {
                          if (hasActiveSubscription) {
                            setModalVisible(true);
                          } else {
                            message.warning('You need an active subscription to start a new interview.');
                          }
                        }}
                        disabled={!hasActiveSubscription}
                      >
                        Start New Interview
                      </Button>
                    </div>
                    <Divider style={{ marginBottom: '15px' }} />
                    <List
                      pagination={{ position: 'bottom', align: 'center', pageSize: 6, showLessItems: true, size: "small" }}
                      dataSource={data}
                      renderItem={(item) => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={<Avatar src={playIcon} />}
                            title={
                              <div style={{ display: 'flex', alignItems: 'center', fontWeight: item._id === selectedId ? 'bold' : 'normal' }}>
                                {item.name}
                                <Tooltip placement="top" title="Delete Patient Entry">
                                  <DeleteOutlined
                                    style={{ color: 'red', marginLeft: '15px', fontSize: '16px', cursor: 'pointer' }}
                                    onClick={() => setOpenConfirmDeleteModal(true)}
                                  />
                                </Tooltip>
                              </div>
                            }
                            description={
                              <span style={{ fontWeight: item._id === selectedId ? 'bold' : 'normal' }}>
                                {(() => {
                                  const istDate = new Date(item.upload_time.replace(' ', 'T') + '+05:30');
                                  const dateInEST = istDate.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
                                  const timeInEST = istDate.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour12: false });
                                  return `${dateInEST} ${timeInEST} (EST)`;
                                })()}
                              </span>
                            }
                            onClick={() => {
                              setSelectedId(item._id);
                              setPatientId(item.patient_id);
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                          {item._id === selectedId && <Badge count="." style={{ backgroundColor: '#00db87', marginLeft: '10px' }} />}
                        </List.Item>
                      )}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col lg={18} sm={24} xs={24} style={{ padding: '0px 10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {patientDataLoading ? (
                <div style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  <Row style={{ display: 'flex', height: '100%' }}>
                    {/* Left Column */}
                    <Col lg={16} sm={24} xs={24} style={{ padding: '10px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      {finalResponse && (
                        <>
                          {/* Summary Card */}
                          <Card
                            style={{
                              padding: '10px',
                              flex: '1 1 auto',
                              display: 'flex',
                              flexDirection: 'column',
                              marginBottom: patientInterview ? '10px' : '0px',
                              overflow: 'auto',
                            }}
                          >
                            <Row>
                              <Col lg={8} md={24} sm={24} xs={24}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography.Title level={5} style={{ margin: 0 }}>
                                    Summary
                                  </Typography.Title>
                                  <Tooltip placement="top" title="Regenerate Summary">
                                    <Button
                                      shape="circle"
                                      type="default"
                                      icon={<RedoOutlined style={{ fontSize: '12px' }} />}
                                      style={{ marginLeft: '15px', width: '30px', height: '30px' }}
                                      onClick={() => {
                                        if (hasActiveSubscription) {
                                          Modal.confirm({
                                            title: 'Confirm Regeneration',
                                            content: 'Do you want to regenerate the summary?',
                                            okText: 'Yes',
                                            cancelText: 'No',
                                            centered: true,
                                            className: themeMode === 'dark' ? 'dark-modal' : 'light-modal',
                                            onOk: () => {
                                              setSynopsisLoading(true);
                                              fetchSummary();
                                            },
                                          });
                                        } else {
                                          message.warning('You need an active subscription to regenerate the summary.');
                                        }
                                      }}
                                      disabled={!hasActiveSubscription}
                                    />
                                  </Tooltip>
                                </div>
                              </Col>
                              <Col lg={16} md={16} sm={24} xs={24} style={{ textAlign: 'right' }}>
                                <Typography.Text style={{ margin: 0, fontSize: '12px', fontWeight: 600 }}>
                                  Selected Template:{' '}
                                </Typography.Text>
                                {selectedTemplate ? (
                                  <Select
                                    size="small"
                                    value={selectedTemplate}
                                    placeholder="Select Template"
                                    style={{ width: '250px' }}
                                    options={templateOptions}
                                    showSearch
                                    optionFilterProp="label"
                                    onChange={(value) => {
                                      Modal.confirm({
                                        title: 'Confirm Template Change',
                                        content: 'Are you sure you want to use this template for this summary?',
                                        okText: 'Yes',
                                        cancelText: 'No',
                                        centered: true,
                                        onOk: async () => {
                                          setSelectedTemplate(value);
                                          const formData = new FormData();
                                          formData.append('template_id', value);
                                          try {
                                            const response = await fetch(
                                              `${process.env.REACT_APP_API_URL}/templates/user/select`,
                                              {
                                                headers: {
                                                  Authorization: `Bearer ${auth.user['access_token']}`,
                                                },
                                                method: 'POST',
                                                body: formData,
                                              }
                                            );
                                            if (response.ok) {
                                              message.success('Template updated and summary is being regenerated.', 7);
                                              setSynopsisLoading(true);
                                              fetchSummary();
                                            } else {
                                              message.error('Failed to update default template');
                                            }
                                          } catch (err) {
                                            console.error('Error updating default template', err);
                                            message.error('Error updating default template');
                                          }
                                        },
                                        onCancel() {},
                                      });
                                    }}
                                  />
                                ) : finalResponse.template_name ? (
                                  finalResponse.template_name
                                ) : (
                                  'Not Mentioned'
                                )}
                              </Col>
                            </Row>
                            <Divider style={{ marginTop: '10px' }} />
                            <div style={{ flexGrow: 1 }}>
                              <Collapse
                                accordion
                                activeKey={[activePanel]}
                                onChange={(v) => setActivePanel(v)}
                              >
                                <Panel header="AI Generated Summary" key="01">
                                  {synopsisLoading ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '20px',
                                      }}
                                    >
                                      <Spin size="large" />
                                    </div>
                                  ) : (
                                    <>
                                      <Row style={{ paddingBottom: '10px' }}>
                                        <Col span={24} style={{ textAlign: 'right' }}>
                                          {finalResponse.summary !== null && (
                                            <IconButtonGroup
                                              text={finalResponse.summary}
                                              feature={{
                                                edit: hasActiveSubscription,
                                                copy: true,
                                                download: true,
                                                email: true,
                                                fullscreen: true,
                                              }}
                                              fileId={selectedId}
                                              setSelectedVersion={setSelectedVersion}
                                              fetchEditedVersions={fetchEditedVersions}
                                              setActivePanel={setActivePanel}
                                              contentType={'summary'}
                                            />
                                          )}
                                        </Col>
                                      </Row>
                                      <div style={{ maxHeight: patientInterview ? '180px' : '430px', overflow: 'auto' }}>
                                        {finalResponse.summary == null ? (
                                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'No Summary Found'} />
                                        ) : (
                                          <MarkdownSectionRenderer markdown={finalResponse.summary} />
                                        )}
                                      </div>
                                    </>
                                  )}
                                </Panel>
                                {versions.length > 0 && (
                                  <Panel header="Edited Summary" key="02">
                                    <Row style={{ paddingBottom: '10px' }}>
                                      <Col lg={8} md={8} sm={24} xs={24}>
                                        <label
                                          style={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            marginTop: '5px',
                                            marginRight: '15px',
                                          }}
                                        >
                                          Select Version:{' '}
                                        </label>
                                        <Select
                                          showSearch
                                          placeholder="Select version"
                                          optionFilterProp="label"
                                          onChange={(v) => setSelectedVersion(v)}
                                          value={selectedVersion}
                                          options={versions}
                                          style={{ width: '50%' }}
                                          disabled={isFeatureDisabled}
                                        />
                                      </Col>
                                      <Col lg={8} md={8} sm={24} xs={24}>
                                        <Typography.Text style={{ margin: 0, fontSize: '12px', fontWeight: 600 }}>
                                          <b style={{ color: '#146ef5' }}>{aiGenerated && 'Regenerated AI Summary'}</b>
                                        </Typography.Text>
                                      </Col>
                                      <Col lg={8} md={8} sm={24} xs={24} style={{ textAlign: 'right' }}>
                                        <IconButtonGroup
                                          text={editedSummary == null ? '' : editedSummary}
                                          feature={{
                                            edit: hasActiveSubscription,
                                            copy: true,
                                            download: true,
                                            email: true,
                                            fullscreen: true,
                                          }}
                                          fileId={selectedId}
                                          setSelectedVersion={setSelectedVersion}
                                          fetchEditedVersions={fetchEditedVersions}
                                          setActivePanel={setActivePanel}
                                          versionId={selectedVersion}
                                          contentType={'summary'}
                                        />
                                      </Col>
                                    </Row>
                                    {editSummaryLoading ? (
                                      <div
                                        style={{
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          display: 'flex',
                                          padding: '20px',
                                        }}
                                      >
                                        <Spin size="large" />
                                      </div>
                                    ) : (
                                      <div style={{ maxHeight: patientInterview ? '180px' : '430px', overflow: 'auto' }}>
                                        <MarkdownSectionRenderer markdown={editedSummary == null ? '' : editedSummary} />
                                      </div>
                                    )}
                                  </Panel>
                                )}
                              </Collapse>
                            </div>
                            <Text style={{ fontSize: '10px' }}>
                              <i>AI can make mistakes. Please share your feedback with us!</i>
                            </Text>
                          </Card>

                          {/* Patient Interview Card */}
                          {patientInterview && (
                            <Card
                              style={{
                                padding: '10px',
                                flex: '1 1 auto',
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '10px',
                              }}
                            >
                              {patientInterviewLoading ? (
                                <div style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', padding: '20px', height: '100%' }}>
                                  <Spin size="large" />
                                </div>
                              ) : (
                                <>
                                  <Row>
                                    <Col span={12}>
                                      <Typography.Title level={5} style={{ margin: 0, padding: 0 }}>
                                        {replaceUnderscoreWithSpace(finalResponse.name)}
                                      </Typography.Title>
                                    </Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                      {/* Placeholder for audio length or other info */}
                                    </Col>
                                  </Row>
                                  <Divider />
                                  <div style={{ flexGrow: 1, overflow: 'auto' }}>
                                    <Waveform audioUrl={finalResponse.file_path} />
                                  </div>
                                </>
                              )}
                            </Card>
                          )}
                        </>
                      )}
                    </Col>

                    {/* Right Column */}
                    <Col lg={8} sm={24} xs={24} style={{ padding: '10px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      {finalResponse && (
                        <>
                          <Collapse accordion activeKey={activeKey} onChange={handleCollapseChange}>
                            <Collapse.Panel
                            header={
                              <Row>
                                <Col xs={24} lg={16}>
                                <Typography.Title level={5} style={{ margin: 0 }}>
                                  ICD-10 Codes
                                </Typography.Title>
                                </Col>
                                <Col xs={24} lg={8} style={{textAlign: "right"}}>
                                  <Tooltip placement="top" title="Regenerate ICD-10 Codes">
                                    <Button
                                      shape="circle"
                                      type="default"
                                      icon={<RedoOutlined style={{ fontSize: '12px' }} />}
                                      style={{ marginLeft: '15px', width: '30px', height: '30px' }}
                                      onClick={(e) => {
                                        if (hasActiveSubscription) {
                                          Modal.confirm({
                                            title: 'Confirm Regeneration',
                                            content: 'Do you want to regenerate the ICD-10 codes?',
                                            okText: 'Yes',
                                            cancelText: 'No',
                                            centered: true,
                                            className: themeMode === 'dark' ? 'dark-modal' : 'light-modal',
                                            onOk: () => {
                                              fetchICD10Codes(true);
                                            },
                                          });
                                        } else {
                                          message.warning('You need an active subscription to regenerate ICD-10 codes.');
                                        }
                                      }}
                                      disabled={!hasActiveSubscription}
                                    />
                                  </Tooltip>
                                  <Tooltip title="Open in Fullscreen">
                                    <Button
                                      shape="circle"
                                      type="default"
                                      icon={<ExpandOutlined style={{ fontSize: '16px' }} />}
                                      style={{ backgroundColor: "transparent", color: '#876603', borderColor: 'transparent',width: "30px", height: "30px", boxShadow: "none", marginLeft: '15px' }}
                                      onClick={() => setIsICD10ModalOpen(true)}
                                    />
                                  </Tooltip>
                                </Col >
                              </Row>}
                            key="icd10"
                            collapsible="icon">
                              <div style={{ height: '420px', overflow: 'auto' }}>
                                {icd10Loading ? (
                                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                                    <Spin size="large" />
                                  </div>
                                ) : (
                                  <Tabs defaultActiveKey="generated">
                                   <Tabs.TabPane tab="Approved" key="approved">
                                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {icd10Codes && icd10Codes.accepted && icd10Codes.accepted.length > 0 ? (
                                          <>
                                            <Button
                                              type="primary"
                                              style={{ backgroundColor: "#fc320a", marginBottom: "8px" }}
                                              disabled={selectedApprovedIds.length === 0}
                                              onClick={async () => {
                                                // Build new accepted IDs by filtering out the ones selected for rejection.
                                                const currentAcceptedIds = icd10Codes.accepted.map((code) => code.id);
                                                const newAcceptedIds = currentAcceptedIds.filter(
                                                  (id) => !selectedApprovedIds.includes(id)
                                                );
                                                await updateICD10Selection(newAcceptedIds);
                                                setSelectedApprovedIds([]);
                                                setPatientDataLoading(true);
                                                fetchPatientData();
                                                setEditedSummaryLoading(true);
                                                fetchSelectedEditedVersion();
                                              }}
                                            >
                                              <b>Reject Selected</b>
                                            </Button>
                                            {icd10Codes.accepted.map((code) => (
                                              <div
                                                key={code.id}
                                                style={{
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'center',
                                                  borderBottom: '1px solid #f0f0f0',
                                                  padding: '8px 0'
                                                }}
                                              >
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <Checkbox
                                                    checked={selectedApprovedIds.includes(code.id)}
                                                    onChange={(e) => {
                                                      if (e.target.checked) {
                                                        setSelectedApprovedIds((prev) => [...prev, code.id]);
                                                      } else {
                                                        setSelectedApprovedIds((prev) => prev.filter((id) => id !== code.id));
                                                      }
                                                    }}
                                                    style={{ marginRight: 8 }}
                                                  />
                                                  <div>
                                                    <Typography.Text strong>
                                                      {code.name} ({code.icd10_code})
                                                    </Typography.Text>
                                                    <div style={{ fontSize: '12px' }}>{code.evidence}</div>
                                                  </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                  <Tooltip title="Copy">
                                                    <CopyOutlined
                                                      onClick={() => {
                                                        navigator.clipboard.writeText(`${code.name} (${code.icd10_code}): ${code.evidence}`);
                                                        message.success('Copied!');
                                                      }}
                                                      style={{
                                                        backgroundColor: "transparent",
                                                        color: '#2196F3',
                                                        borderColor: 'transparent',
                                                        width: "30px",
                                                        height: "30px",
                                                        boxShadow: "none",
                                                        cursor: "pointer"
                                                      }}
                                                    />
                                                  </Tooltip>
                                                </div>
                                              </div>
                                            ))}
                                          </>
                                        ) : (
                                          <Empty description="No approved ICD-10 codes" />
                                        )}
                                      </div>
                                    </Tabs.TabPane>

                                    <Tabs.TabPane tab="Generated" key="generated">
                                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                      {icd10Codes && icd10Codes.generated && icd10Codes.generated.length === 0 ? (
                                        <Empty description="No unselected ICD-10 codes" />
                                      ) : (
                                        <>
                                          {/* Determine the unprocessed codes */}
                                          {(() => {
                                            const unprocessedCodes = (icd10Codes.generated || []).filter(
                                              (code) =>
                                                !(
                                                  (icd10Codes.accepted || []).some((acc) => acc.id === code.id) ||
                                                  (icd10Codes.rejected || []).some((rej) => rej.id === code.id)
                                                )
                                            );

                                            return (
                                              <>
                                                {/* Render the button only if there are unprocessed codes */}
                                                {unprocessedCodes.length > 0 && (
                                                  <Button
                                                    type="primary"
                                                    style={{ backgroundColor: "#45c655", marginBottom: "8px" }}
                                                    disabled={idsToBeApproved.length === 0}
                                                    onClick={()=>{
                                                      updateICD10Selection(idsToBeApproved)
                                                    }}
                                                  >
                                                    <b>Approve Selected</b>
                                                  </Button>
                                                )}
                                                {/* Render all generated codes */}
                                                {(icd10Codes.generated || []).map((code) => {
                                                  const isProcessed =
                                                    (icd10Codes.accepted || []).some((acc) => acc.id === code.id) ||
                                                    (icd10Codes.rejected || []).some((rej) => rej.id === code.id);

                                                  return isProcessed ? (
                                                    <ICD10CodeItemReadOnly key={code.id} code={code} status="generated" />
                                                  ) : (
                                                    <ICD10CodeItemWithCheckbox
                                                      key={code.id}
                                                      code={code}
                                                      status="generated"
                                                      onApprove={handleICD10Approve}
                                                      onReject={handleICD10Reject}
                                                      selected={idsToBeApproved.includes(code.id)}
                                                    />
                                                  );
                                                })}
                                              </>
                                            );
                                          })()}
                                        </>
                                      )}
                                      </div>
                                    </Tabs.TabPane>

                                    <Tabs.TabPane tab="Rejected" key="rejected">
                                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {icd10Codes && icd10Codes.rejected && icd10Codes.rejected.length > 0 ? (
                                          <>
                                            <Button
                                              type="primary"
                                              style={{ backgroundColor: "#45c655", marginBottom: "8px" }}
                                              disabled={selectedRejectedIds.length === 0}
                                              onClick={async () => {
                                                // Add selected rejected codes to the current accepted list.
                                                const currentAcceptedIds = icd10Codes.accepted.map((code) => code.id);
                                                // Union current accepted with selected rejected ids.
                                                const newAcceptedIds = Array.from(
                                                  new Set([...currentAcceptedIds, ...selectedRejectedIds])
                                                );
                                                await updateICD10Selection(newAcceptedIds);
                                                setSelectedRejectedIds([]);
                                                setPatientDataLoading(true);
                                                fetchPatientData();
                                                setEditedSummaryLoading(true);
                                                fetchSelectedEditedVersion();
                                              }}
                                            >
                                              <b>Approve Selected</b>
                                            </Button>
                                            {icd10Codes.rejected.map((code) => (
                                              <div
                                                key={code.id}
                                                style={{
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'center',
                                                  borderBottom: '1px solid #f0f0f0',
                                                  padding: '8px 0'
                                                }}
                                              >
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <Checkbox
                                                    checked={selectedRejectedIds.includes(code.id)}
                                                    onChange={(e) => {
                                                      if (e.target.checked) {
                                                        setSelectedRejectedIds((prev) => [...prev, code.id]);
                                                      } else {
                                                        setSelectedRejectedIds((prev) => prev.filter((id) => id !== code.id));
                                                      }
                                                    }}
                                                    style={{ marginRight: 8 }}
                                                  />
                                                  <div>
                                                    <Typography.Text strong>
                                                      {code.name} ({code.icd10_code})
                                                    </Typography.Text>
                                                    <div style={{ fontSize: '12px' }}>{code.evidence}</div>
                                                  </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                  <Tooltip title="Copy">
                                                    <CopyOutlined
                                                      onClick={() => {
                                                        navigator.clipboard.writeText(`${code.name} (${code.icd10_code}): ${code.evidence}`);
                                                        message.success('Copied!');
                                                      }}
                                                      style={{
                                                        backgroundColor: "transparent",
                                                        color: '#2196F3',
                                                        borderColor: 'transparent',
                                                        width: "30px",
                                                        height: "30px",
                                                        boxShadow: "none",
                                                        cursor: "pointer"
                                                      }}
                                                    />
                                                  </Tooltip>
                                                </div>
                                              </div>
                                            ))}
                                          </>
                                        ) : (
                                          <Empty description="No rejected ICD-10 codes" />
                                        )}
                                      </div>
                                    </Tabs.TabPane>

                                  </Tabs>
                                )}
                              </div>
                            </Collapse.Panel>
                            <Collapse.Panel
                            header={
                              <Row>
                                {/* LEFT SIDE: Title + Icons */}
                                <Col xs={24} lg={16}>
                                  <Typography.Title level={5} style={{ margin: 0 }}>
                                    Transcript
                                  </Typography.Title>

                                </Col>
                                <Col xs={24} lg={8} style={{textAlign: "right"}}>
                                <Tooltip placement="top" title="Regenerate Transcription">
                                    <Button
                                      shape="circle"
                                      type="default"
                                      icon={<RedoOutlined style={{ fontSize: '12px' }} />}
                                      style={{ marginLeft: '15px', width: '30px', height: '30px' }}
                                      onClick={() => {
                                        if (hasActiveSubscription) {
                                          Modal.confirm({
                                            title: 'Confirm Regeneration',
                                            content: 'Do you want to regenerate the transcription?',
                                            okText: 'Yes',
                                            cancelText: 'No',
                                            centered: true,
                                            className: themeMode === 'dark' ? 'dark-modal' : 'light-modal',
                                            onOk: () => {
                                              setTranscriptionLoading(true);
                                              fetchTranscript();
                                            },
                                          });
                                        } else {
                                          message.warning('You need an active subscription to regenerate the transcription.');
                                        }
                                      }}
                                      disabled={!hasActiveSubscription}
                                    />
                                  </Tooltip>
                                  <Tooltip placement="top" title="Fetch Interview Recording">
                                    <Button
                                      size="small"
                                      shape="circle"
                                      type="default"
                                      icon={<CaretRightOutlined />}
                                      style={{
                                        marginLeft: '15px',
                                        backgroundColor: 'transparent',
                                        color: '#2196F3',
                                        borderColor: '#2196F3',
                                      }}
                                      onClick={() => {
                                        if (hasActiveSubscription) {
                                          Modal.confirm({
                                            title: 'Confirm Action',
                                            content: 'Do you want to fetch the interview recording?',
                                            okText: 'Yes',
                                            cancelText: 'No',
                                            centered: true,
                                            className: themeMode === 'dark' ? 'dark-modal' : 'light-modal',
                                            onOk: () => {
                                              setPatientInterviewLoading(true);
                                              fetchPatientInterview(selectedId);
                                            },
                                          });
                                        } else {
                                          message.warning('You need an active subscription to fetch interview recordings.');
                                        }
                                      }}
                                      loading={patientInterviewLoading}
                                      disabled={!hasActiveSubscription}
                                    />
                                  </Tooltip>
                                </Col>
                              </Row>
                            }
                            key="transcript"
                            collapsible="icon">
                              <Row style={{ marginBottom: '5px' }}>
                                <Col span={24} style={{ textAlign: "right"}}>
                                  {finalResponse.transcription !== null && (
                                    <IconButtonGroup
                                      text={
                                        finalResponse.transcription[selectedLanguage] == null
                                          ? ''
                                          : finalResponse.transcription[selectedLanguage]
                                      }
                                      feature={{
                                        edit: false,
                                        copy: true,
                                        download: true,
                                        email: false,
                                        fullscreen: true,
                                      }}
                                      fileId={selectedId}
                                      contentType="transcript"
                                    />
                                  )}
                                </Col>
                              </Row>
                              <Divider style={{ margin: 0 }} />
                              <div style={{ flexGrow: 1 }}>
                                <div style={{ height: '385px', overflow: 'auto' }}>
                                  {transcriptionLoading ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '20px',
                                      }}
                                    >
                                      <Spin size="large" />
                                    </div>
                                  ) : finalResponse.transcription == null ? (
                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'No Transcription Found'} />
                                  ) : (
                                    <div
                                      className="message-content"
                                      dangerouslySetInnerHTML={{
                                        __html: markdownToHtml(
                                          finalResponse.transcription[selectedLanguage] == null
                                            ? ''
                                            : finalResponse.transcription[selectedLanguage]
                                        ),
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            </Collapse.Panel>
                          </Collapse>


                          {/* Action Card */}
                          <Card style={{ padding: '10px', marginTop: '10px', display: 'flex', flexDirection: 'column' }}>
                            <Row>
                              <Col span={24}>
                                <Typography.Title level={5} style={{ margin: 0, padding: 0 }}>
                                  Action
                                </Typography.Title>
                              </Col>
                            </Row>
                            <Divider />
                            <Row style={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                              <Col lg={8} sm={24} xs={24} style={{ textAlign: 'center' }}>
                                <Tooltip placement="top" title="Generate Assessment Report">
                                  <Button
                                    type="default"
                                    style={{ height: '50px', width: '50px', borderColor: '#0074D9', marginBottom: '5px' }}
                                    onClick={() => {
                                      if (hasActiveSubscription) {
                                        setIsAssessmentModalOpen(true);
                                      } else {
                                        message.warning('You need an active subscription to generate assessment reports.');
                                      }
                                    }}
                                    disabled={!hasActiveSubscription}
                                  >
                                    <FileTextOutlined style={{ fontSize: '30px', color: '#0074D9' }} />
                                  </Button>
                                </Tooltip>
                              </Col>
                              <Col lg={8} sm={24} xs={24} style={{ textAlign: 'center' }}>
                                <Tooltip placement="top" title="View Patient Details">
                                  <Button
                                    type="default"
                                    style={{ height: '50px', width: '50px', borderColor: '#0074D9', marginBottom: '5px' }}
                                    onClick={() => setIsEditModalOpen(true)}
                                    disabled={!hasActiveSubscription}
                                  >
                                    <FolderViewOutlined style={{ fontSize: '30px', color: '#0074D9' }} />
                                  </Button>
                                </Tooltip>
                              </Col>
                              <Col lg={8} sm={24} xs={24} style={{ textAlign: 'center' }}>
                                <Tooltip placement="top" title="View Patient Visit Summary">
                                  <Button
                                    type="default"
                                    style={{ height: '50px', width: '50px', borderColor: '#0074D9', marginBottom: '5px' }}
                                    onClick={() => {
                                      if (hasActiveSubscription) {
                                        setIsPatientNoteModalOpen(true);
                                      } else {
                                        message.warning('You need an active subscription to view patient notes.');
                                      }
                                    }}
                                    disabled={!hasActiveSubscription || !finalResponse.summary}
                                  >
                                    {sendEmailLoading ? (
                                      <LoadingOutlined style={{ fontSize: '30px', color: '#0074D9' }} />
                                    ) : (
                                      <ProfileOutlined style={{ fontSize: '30px', color: '#0074D9' }} />
                                    )}
                                  </Button>
                                </Tooltip>
                              </Col>
                            </Row>
                          </Card>
                        </>
                      )}
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Row>
        )}
        {/* Payment Modal */}
        <PaymentModal
          visible={isPaymentModalOpen}
          onCancel={handlePaymentCancel}
          onPay={handlePaymentAction}
          loading={paymentLoading}
          action={paymentAction}
          onSuccess={handlePaymentSuccess}
        />
        {/* AddTemplate Modal */}
        <AddTemplateModal
          visible={addTemplateModalVisible}
          onCancel={handleAddTemplateCancel}
          onSave={handleAddTemplateSave}
          saving={addTemplateSaving}
          form={addTemplateForm}
          themeMode={themeMode}
          mandatory={true}
        />
        {/* Other Modals */}
        <RecordingModal
          open={modalVisible}
          setOpen={setModalVisible}
          setSelectedId={setSelectedId}
          fetchData={fetchData}
          setNewSubmission={setNewSubmission}
          setSelectedLanguage={setSelectedLanguage}
        />
        <EditPatientModal open={isEditModalOpen} setOpen={setIsEditModalOpen} patientId={patientId} />
        <AssessmentModal open={isAssessmentModalOpen} setOpen={setIsAssessmentModalOpen} fileId={selectedId} />
        <PatientNoteModal
          open={isPatientNoteModalOpen}
          setOpen={setIsPatientNoteModalOpen}
          fileId={selectedId}
          handleSendEmail={handleSendEmail}
          sendEmailLoading={sendEmailLoading}
        />
        <ConfirmDeletePatientModal
          open={openConfirmDeleteModal}
          setOpen={setOpenConfirmDeleteModal}
          handleDeletePatient={handleDeletePatient}
          loading={deletePatientEntryLoading}
        />
        {finalResponse && finalResponse.summary && <StickyChat title="Copilot" fileId={selectedId} patientId={patientId} type={"summary"} />}
        {/* NEW: ICD-10 Codes Fullscreen Modal */}
        <Modal
          title="ICD-10 Code"
          open={isICD10ModalOpen}
          onCancel={() => setIsICD10ModalOpen(false)}
          footer={null}
          width="80%"
        >
          {icd10Loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Tabs defaultActiveKey="generated">
              <Tabs.TabPane tab="Approved" key="approved">
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {icd10Codes && icd10Codes.accepted && icd10Codes.accepted.length > 0 ? (
                    <>
                      <Button
                        type="primary"
                        style={{ backgroundColor: "#fc320a", marginBottom: "8px" }}
                        disabled={selectedApprovedIds.length === 0}
                        onClick={async () => {
                          // Build new accepted IDs by filtering out the ones selected for rejection.
                          const currentAcceptedIds = icd10Codes.accepted.map((code) => code.id);
                          const newAcceptedIds = currentAcceptedIds.filter(
                            (id) => !selectedApprovedIds.includes(id)
                          );
                          await updateICD10Selection(newAcceptedIds);
                          setSelectedApprovedIds([]);
                          setPatientDataLoading(true);
                          fetchPatientData();
                          setEditedSummaryLoading(true);
                          fetchSelectedEditedVersion();
                        }}
                      >
                        <b>Reject Selected</b>
                      </Button>
                      {icd10Codes.accepted.map((code) => (
                        <div
                          key={code.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #f0f0f0',
                            padding: '8px 0'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              checked={selectedApprovedIds.includes(code.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedApprovedIds((prev) => [...prev, code.id]);
                                } else {
                                  setSelectedApprovedIds((prev) => prev.filter((id) => id !== code.id));
                                }
                              }}
                              style={{ marginRight: 8 }}
                            />
                            <div>
                              <Typography.Text strong>
                                {code.name} ({code.icd10_code})
                              </Typography.Text>
                              <div style={{ fontSize: '12px' }}>{code.evidence}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <Tooltip title="Copy">
                              <CopyOutlined
                                onClick={() => {
                                  navigator.clipboard.writeText(`${code.name} (${code.icd10_code}): ${code.evidence}`);
                                  message.success('Copied!');
                                }}
                                style={{
                                  backgroundColor: "transparent",
                                  color: '#2196F3',
                                  borderColor: 'transparent',
                                  width: "30px",
                                  height: "30px",
                                  boxShadow: "none",
                                  cursor: "pointer"
                                }}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <Empty description="No approved ICD-10 codes" />
                  )}
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab="Generated" key="generated">
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {icd10Codes && icd10Codes.generated && icd10Codes.generated.length === 0 ? (
                  <Empty description="No unselected ICD-10 codes" />
                ) : (
                  <>
                    {/* Determine the unprocessed codes */}
                    {(() => {
                      const unprocessedCodes = (icd10Codes.generated || []).filter(
                        (code) =>
                          !(
                            (icd10Codes.accepted || []).some((acc) => acc.id === code.id) ||
                            (icd10Codes.rejected || []).some((rej) => rej.id === code.id)
                          )
                      );

                      return (
                        <>
                          {/* Render the button only if there are unprocessed codes */}
                          {unprocessedCodes.length > 0 && (
                            <Button
                              type="primary"
                              style={{ backgroundColor: "#45c655", marginBottom: "8px" }}
                              disabled={idsToBeApproved.length === 0}
                              onClick={()=>{
                                updateICD10Selection(idsToBeApproved)
                              }}
                            >
                              <b>Approve Selected</b>
                            </Button>
                          )}

                          {/* Render all generated codes */}
                          {(icd10Codes.generated || []).map((code) => {
                            const isProcessed =
                              (icd10Codes.accepted || []).some((acc) => acc.id === code.id) ||
                              (icd10Codes.rejected || []).some((rej) => rej.id === code.id);

                            return isProcessed ? (
                              <ICD10CodeItemReadOnly key={code.id} code={code} status="generated" />
                            ) : (
                              <ICD10CodeItemWithCheckbox
                                key={code.id}
                                code={code}
                                status="generated"
                                onApprove={handleICD10Approve}
                                onReject={handleICD10Reject}
                                selected={idsToBeApproved.includes(code.id)}
                              />
                            );
                          })}
                        </>
                      );
                    })()}
                  </>
                )}
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab="Rejected" key="rejected">
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {icd10Codes && icd10Codes.rejected && icd10Codes.rejected.length > 0 ? (
                    <>
                      <Button
                        type="primary"
                        style={{ backgroundColor: "#45c655", marginBottom: "8px" }}
                        disabled={selectedRejectedIds.length === 0}
                        onClick={async () => {
                          // Add selected rejected codes to the current accepted list.
                          const currentAcceptedIds = icd10Codes.accepted.map((code) => code.id);
                          // Union current accepted with selected rejected ids.
                          const newAcceptedIds = Array.from(
                            new Set([...currentAcceptedIds, ...selectedRejectedIds])
                          );
                          await updateICD10Selection(newAcceptedIds);
                          setSelectedRejectedIds([]);
                          setPatientDataLoading(true);
                          fetchPatientData();
                          setEditedSummaryLoading(true);
                          fetchSelectedEditedVersion();
                        }}
                      >
                        <b>Approve Selected</b>
                      </Button>
                      {icd10Codes.rejected.map((code) => (
                        <div
                          key={code.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #f0f0f0',
                            padding: '8px 0'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              checked={selectedRejectedIds.includes(code.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRejectedIds((prev) => [...prev, code.id]);
                                } else {
                                  setSelectedRejectedIds((prev) => prev.filter((id) => id !== code.id));
                                }
                              }}
                              style={{ marginRight: 8 }}
                            />
                            <div>
                              <Typography.Text strong>
                                {code.name} ({code.icd10_code})
                              </Typography.Text>
                              <div style={{ fontSize: '12px' }}>{code.evidence}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <Tooltip title="Copy">
                              <CopyOutlined
                                onClick={() => {
                                  navigator.clipboard.writeText(`${code.name} (${code.icd10_code}): ${code.evidence}`);
                                  message.success('Copied!');
                                }}
                                style={{
                                  backgroundColor: "transparent",
                                  color: '#2196F3',
                                  borderColor: 'transparent',
                                  width: "30px",
                                  height: "30px",
                                  boxShadow: "none",
                                  cursor: "pointer"
                                }}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <Empty description="No rejected ICD-10 codes" />
                  )}
                </div>
              </Tabs.TabPane>

            </Tabs>
          )}
        </Modal>
      </Content>
    </>
  );
};

export default Synopsis;
